import { Box, HStack, ListIcon, ListItem } from "@chakra-ui/react";
import { RoundToggleButton } from "@components/ui-kit";
import {
  useMarkNotificationAsReadMutation,
  useMarkNotificationAsUnreadMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import cs from "classnames";
import { observer } from "mobx-react-lite";
import type { NotificationModel } from "../../models";
import { Icon } from "./Icon";
import Message from "./Message";

interface NotificationItemProps {
  isFirst: boolean;
  item: NotificationModel;
  onAction: (val: NotificationModel) => void;
}

const NotificationItem = ({
  isFirst,
  item,
  onAction,
}: NotificationItemProps) => {
  const { notificationsStore: store } = useStore();

  const [markAsRead, markAsReadResult] = useMarkNotificationAsReadMutation({
    variables: { id: item.id },
    onCompleted({ markNotificationAsRead }) {
      if (markNotificationAsRead) {
        item.setIsRead(true);
        store.decrementUnreadCount();
      }
    },
    ...onError(),
  });

  const [markAsUnread, markAsUnreadResult] =
    useMarkNotificationAsUnreadMutation({
      variables: { id: item.id },
      onCompleted({ markNotificationAsUnread }) {
        if (markNotificationAsUnread) {
          item.setIsRead(false);
          store.incrementUnreadCount();
        }
      },
      ...onError(),
    });

  const handleToggle = () => {
    if (item.isRead) {
      markAsUnread();
    } else {
      markAsRead();
    }
  };

  const handleAction = async () => {
    if (!item.isRead) {
      await markAsRead();
    }
    onAction(item);
  };

  const loading = markAsReadResult.loading || markAsUnreadResult.loading;

  return (
    <div className={cs({ "bg-skyLight": !!item.isRead })}>
      <ListItem
        px="2rem"
        py="1.2rem"
        borderTopWidth={isFirst ? 0 : "0.1rem"}
        borderTopStyle="solid"
        borderTopColor="grey.100"
        _hover={{ bg: "grey.50" }}
        cursor="pointer"
        id={item.id}
        onClick={handleAction}
      >
        <HStack>
          <ListIcon as={Icon} />
          <Message message={item.message} createdAt={item.createdAt} />
          <Box
            pos="absolute"
            right="3"
            ml="auto"
            onClick={(e) => e.stopPropagation()}
          >
            <RoundToggleButton
              loading={loading}
              isActive={!!item.isRead}
              onToggle={handleToggle}
            />
          </Box>
        </HStack>
      </ListItem>
    </div>
  );
};

export default observer(NotificationItem);
